import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, ChevronRight, Users } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/authService";

type DemoUser = {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  role_name?: string;
  title?: string;
};

type DemoUserSwitcherProps = {
  trigger?: "button" | "menu-item";
};

export function DemoUserSwitcher({ trigger = "button" }: DemoUserSwitcherProps) {
  const { isDemo, isImpersonating, originalUser, impersonate, stopImpersonating } = useAuth();
  const queryClient = useQueryClient();
  const [users, setUsers] = useState<DemoUser[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (roleName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  };

  useEffect(() => {
    if (open && isDemo) {
      authService.getDemoUsers().then(setUsers).catch(() => setUsers([]));
    }
  }, [open, isDemo]);

  if (!isDemo) return null;

  const SYSTEM_ROLE_ORDER = ["Owner", "Admin", "Member"];

  const groupedUsers = (() => {
    const groups = new Map<string, DemoUser[]>();
    for (const user of users) {
      const role = user.role_name || "No Role";
      if (!groups.has(role)) groups.set(role, []);
      groups.get(role)!.push(user);
    }
    return [...groups.entries()].sort(([a], [b]) => {
      const ai = SYSTEM_ROLE_ORDER.indexOf(a);
      const bi = SYSTEM_ROLE_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  })();

  const getInitials = (user: DemoUser) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  const getDisplayName = (user: DemoUser) =>
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;

  const handleImpersonate = async (userId: string) => {
    setLoading(true);
    try {
      await impersonate(userId);
      await queryClient.invalidateQueries();
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonating = async () => {
    setLoading(true);
    try {
      await stopImpersonating();
      await queryClient.invalidateQueries();
    } finally {
      setLoading(false);
    }
  };

  const originalName = originalUser
    ? [originalUser.first_name, originalUser.last_name].filter(Boolean).join(" ") || originalUser.email
    : "";

  if (isImpersonating) {
    if (trigger === "menu-item") {
      return (
        <DropdownMenuItem
          onSelect={() => {
            void handleStopImpersonating();
          }}
          disabled={loading}
          className="text-amber-600 focus:text-amber-700 dark:text-amber-400"
        >
          <ArrowLeftRight className="mr-2 h-4 w-4" />
          Back to {originalName}
        </DropdownMenuItem>
      );
    }

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleStopImpersonating}
        disabled={loading}
        className="gap-1.5 border-amber-500/50 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
      >
        <ArrowLeftRight className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Back to {originalName}</span>
        <span className="sm:hidden">Switch Back</span>
      </Button>
    );
  }

  const userList = (
    <>
      <DropdownMenuLabel className="text-xs text-muted-foreground">
        View as another user
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {users.length === 0 ? (
        <div className="px-2 py-3 text-center text-sm text-muted-foreground">No other users</div>
      ) : null}
      {groupedUsers.map(([roleName, groupUsers], idx) => (
        <div key={roleName}>
          {idx > 0 ? <DropdownMenuSeparator /> : null}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleGroup(roleName);
            }}
            className="flex w-full cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent"
          >
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
                expandedGroups.has(roleName) ? "rotate-90" : ""
              }`}
            />
            <span className="flex-1 text-left">{roleName}</span>
            <span className="ml-auto rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-normal">
              {groupUsers.length}
            </span>
          </button>
          {expandedGroups.has(roleName) ? (
            <DropdownMenuGroup>
              {groupUsers.map((user) => (
                <DropdownMenuItem
                  key={user.user_id}
                  onClick={() => handleImpersonate(user.user_id)}
                  disabled={loading}
                  className="flex cursor-pointer items-center gap-3 py-2.5"
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="bg-muted text-xs font-medium">
                      {getInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{getDisplayName(user)}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user.title || user.email}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          ) : null}
        </div>
      ))}
    </>
  );

  if (trigger === "menu-item") {
    return (
      <DropdownMenuSub open={open} onOpenChange={setOpen}>
        <DropdownMenuSubTrigger>
          <Users className="mr-2 h-4 w-4" />
          Switch User
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="max-h-[70vh] w-72 overflow-y-auto">
          {userList}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Users className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Switch User</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
        {userList}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
